/**
 * Выделение → действие: выделил текст в нарративе — всплывает мини-панель
 * «Спросить агента» / «Прокомментировать». Оба действия открывают чат
 * с автоцитатой (комментарий публикуется через post_comment с UI-подтверждением).
 *
 * Работает для выделений внутри элементов с атрибутом `data-quote-scope`
 * (навешивается на колонку нарратива). Позиция — над концом выделения.
 */

import { useEffect, useState } from 'react';
import { Button, Group, Paper } from '@mantine/core';
import { IconMessageCircle, IconRobot } from '@tabler/icons-react';
import { useUiStore } from '../stores/ui-store';

interface Pos { top: number; left: number }

export function SelectionActions(): JSX.Element | null {
    const requestChat = useUiStore(s => s.requestChat);
    const [state, setState] = useState<{ pos: Pos; text: string } | null>(null);

    useEffect(() => {
        const onMouseUp = (): void => {
            // Даём браузеру зафиксировать выделение.
            setTimeout(() => {
                const sel = window.getSelection();
                const text = sel?.toString().trim() ?? '';
                if (!sel || sel.isCollapsed || text.length < 8 || text.length > 600) {
                    setState(null);
                    return;
                }
                // Только внутри размеченной зоны (нарратив), не в чате/панелях.
                const node = sel.anchorNode instanceof Element ? sel.anchorNode : sel.anchorNode?.parentElement;
                if (!node?.closest('[data-quote-scope]')) {
                    setState(null);
                    return;
                }
                const rect = sel.getRangeAt(0).getBoundingClientRect();
                setState({
                    pos:  { top: rect.top + window.scrollY - 40, left: rect.left + window.scrollX + rect.width / 2 },
                    text,
                });
            }, 10);
        };
        const onDown = (e: MouseEvent): void => {
            // Клик по самой панели не должен её закрывать.
            if ((e.target as Element | null)?.closest?.('[data-selection-actions]')) return;
            setState(null);
        };
        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('mousedown', onDown);
        return () => {
            document.removeEventListener('mouseup', onMouseUp);
            document.removeEventListener('mousedown', onDown);
        };
    }, []);

    if (!state) return null;

    const quote = state.text.length > 280 ? state.text.slice(0, 280) + '…' : state.text;
    const act = (prefix: string): void => {
        requestChat(`${prefix}\n\n> ${quote.replace(/\n/g, '\n> ')}\n\n`);
        setState(null);
        window.getSelection()?.removeAllRanges();
    };

    return (
        <Paper
            data-selection-actions
            shadow="md"
            radius="md"
            p={4}
            withBorder
            style={{
                position: 'absolute',
                top: state.pos.top,
                left: state.pos.left,
                transform: 'translateX(-50%)',
                zIndex: 400,
            }}
        >
            <Group gap={4} wrap="nowrap">
                <Button size="compact-xs" variant="subtle" leftSection={<IconRobot size={13} />}
                    onClick={() => act('Про этот фрагмент нарратива:')}>
                    Спросить агента
                </Button>
                <Button size="compact-xs" variant="subtle" leftSection={<IconMessageCircle size={13} />}
                    onClick={() => act('Оставь комментарий к этому фрагменту:')}>
                    Комментировать
                </Button>
            </Group>
        </Paper>
    );
}
